import { OuiIconButtonComponent } from '@aiz-gl/omni-ui';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { TEXT } from '../../constants/text';
import { AuthService } from '../../services/auth/auth.service';
import { CasbinPermissionsService } from '../../services/feature/casbin-permissions.service';
import { DccFeatureAccessService } from '../../services/feature/dcc-feature-access.service';
import { FeatureAccessService } from '../../services/feature/feature-access.service';
import { UserDccsService } from '../../services/user-dccs/user-dccs.service';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

export interface NavGroup {
  label: string;
  icon: string;
  children?: NavItem[];
  route?: string;
  permission?: string;
  featureFlag?: string;
}

export interface NavItem {
  label: string;
  icon: string;
  route: string;
  permission?: string;
  featureFlag?: string;
  noLock?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [OuiIconButtonComponent, SvgIconComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  @Input() open = false;
  @Input() collapsed = false;
  @Output() sidebarClosed = new EventEmitter<void>();

  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly permissionsService = inject(CasbinPermissionsService);
  private readonly dccFeatureAccessService = inject(DccFeatureAccessService);
  private readonly featureAccessService = inject(FeatureAccessService);
  private readonly userDccsService = inject(UserDccsService);

  ngOnInit(): void {
    // Load the per-DCC feature flags so the nav can hide features that are
    // unavailable for the selected DCC (e.g. Device Photo for certain DCCs).
    void this.dccFeatureAccessService.ensureLoaded().catch((error) => {
      console.error('Failed to load DCC feature access configuration', error);
    });
  }

  protected readonly T = TEXT;

  navGroups: NavGroup[] = [
    {
      label: TEXT.sidebar.sections.management,
      icon: 'dashboard',
      children: [
        {
          label: TEXT.sidebar.items.dashboard,
          icon: 'dashboard',
          route: 'dashboard',
        },
      ],
    },
    {
      label: TEXT.sidebar.sections.dockReceiving,
      icon: 'package',
      children: [
        {
          label: TEXT.sidebar.items.receivePackages,
          icon: 'package',
          route: 'dock-receiving',
          permission: 'package:manage',
          featureFlag: 'Receive Packages',
        },
        {
          label: TEXT.sidebar.items.asnDashboard,
          icon: 'receipt',
          route: 'asn-dashboard',
          permission: 'package:view',
          featureFlag: 'Dock Search',
        },
        {
          label: TEXT.sidebar.items.search,
          icon: 'search',
          route: 'search-dock',
          permission: 'package:view',
          featureFlag: 'Dock Search',
        },
      ],
    },
    {
      label: TEXT.sidebar.sections.itemReceiving,
      icon: 'dock',
      children: [
        {
          label: TEXT.sidebar.items.deviceControlCenter,
          icon: 'dashboard',
          route: 'device-control-center',
          permission: 'devicecontrolcenter:manage',
          featureFlag: 'Device Control Center',
        },
        {
          label: TEXT.sidebar.items.receiveItems,
          icon: 'dock',
          route: 'item-receiving',
          permission: 'photocapture:manage',
          featureFlag: 'Device Photo',
        },
        {
          label: TEXT.sidebar.items.devicePhotoSearch,
          icon: 'search',
          route: 'device-photo-search',
          permission: 'photocapture:view',
          featureFlag: 'Device Photo',
        },
        {
          label: TEXT.sidebar.items.secondaryClearDashboard,
          icon: 'dashboard',
          route: 'secondary-clear-dashboard',
          permission: 'secondaryclearing:view',
          featureFlag: 'Secondary Clear Dashboard',
        },
        {
          label: TEXT.sidebar.items.secondaryClearProcessing,
          icon: 'package',
          route: 'secondary-clear-processing',
          permission: 'secondaryclearing:manage',
          featureFlag: 'Secondary Clear Processing',
        },
      ],
    },
    {
      label: TEXT.sidebar.sections.rma,
      icon: 'package',
      children: [
        {
          label: TEXT.sidebar.items.rmaDashboard,
          icon: 'dashboard',
          route: 'rma-dashboard',
          permission: 'rmadashboard:view',
          featureFlag: 'RMA Dashboard',
        },
        {
          label: TEXT.sidebar.items.salesRmaProcessing,
          icon: 'receipt',
          route: 'sales-rma-processing',
          permission: 'rma:manage',
          featureFlag: 'Sales RMA Processing',
        },
        {
          label: TEXT.sidebar.items.searchRma,
          icon: 'search',
          route: 'rma-search',
          permission: 'rma:view',
          featureFlag: 'Search RMA',
        },
      ],
    },
    {
      label: TEXT.sidebar.sections.userManagement,
      icon: 'users',
      permission: 'admin',
      children: [
        {
          label: TEXT.sidebar.items.userList,
          icon: 'users',
          route: 'user-role',
          noLock: true,
        },
      ],
    },
    {
      label: TEXT.sidebar.sections.configuration,
      icon: 'settings',
      children: [
        {
          label: TEXT.sidebar.items.dccConfiguration,
          icon: 'settings',
          route: 'dcc-configuration',
          permission: 'admin',
          noLock: true,
        },
      ],
    },
  ];

  /**
   * Nav groups filtered to only what the current user is allowed to see.
   * Reads the permission, selected-DCC, and DCC feature-flag signals so it
   * recomputes when policies load, the DCC changes, or flags load.
   */
  visibleNavGroups = computed<NavGroup[]>(() => {
    // Touch the signal so this computed re-runs when permissions change.
    this.permissionsService.permissionSet();

    return this.navGroups
      .filter((group) => this.canAccess(group))
      .map((group) => ({
        ...group,
        children: group.children?.filter((child) => this.canAccess(child)),
      }))
      .filter((group) => !group.children || group.children.length > 0);
  });

  /**
   * Whether the current user can access a nav item. Delegates to the shared
   * FeatureAccessService so sidebar visibility stays consistent with the route
   * guards and the dashboard landing logic.
   */
  canAccess(item: { permission?: string; featureFlag?: string }): boolean {
    return this.featureAccessService.canAccess(item, this.dccCode);
  }

  get dccCode(): string | undefined {
    return this.userDccsService.getUserAssignedDcc() ?? undefined;
  }

  get isLocationSelected(): boolean {
    return !!this.dccCode;
  }

  /** True whenever nav items (except Dashboard) should be disabled.
   *  Locked only while no DCC is active. A single/default DCC is auto-selected
   *  on the dashboard "welcome" landing, so nav is enabled there; the multi-DCC
   *  picker leaves the DCC unset until the user chooses, keeping nav locked. */
  get navItemsLocked(): boolean {
    return !this.isLocationSelected;
  }

  isActive(route: string): boolean {
    // Match the exact path segment, not a substring — otherwise routes like
    // "secondary-clear-dashboard" would also mark "dashboard" as active.
    const segments = this.router.url
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter(Boolean);
    return segments.includes(route);
  }

  navigate(item: NavItem): void {
    if (item.route === 'dashboard') {
      this.router.navigate(['/dashboard']);
    } else if (item.noLock) {
      this.router.navigate(['/' + item.route]);
    } else {
      if (!this.dccCode) return;
      this.router.navigate(['/dock-menu', this.dccCode, item.route]);
    }
    this.sidebarClosed.emit();
  }

  navigateGroup(group: NavGroup): void {
    if (!this.dccCode || !group.route) return;
    this.router.navigate(['/dock-menu', this.dccCode, group.route]);
    this.sidebarClosed.emit();
  }

  goToDashboard(): void {
    this.router.navigate(['/dashboard']);
    this.sidebarClosed.emit();
  }

  signOut(): void {
    void this.authService.signOut();
  }
}
